import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Account, IProduct, TEmailHealth } from '@feathr/blackbox';
import { showError, toast } from '@feathr/components';
import { useAccount, useActionBar, useLocalUrl, useStore } from '@feathr/extender/state';

import type { IErrors, IIndustryCategory } from './ProfilePage.utils';
import { getPickerOptions, VALIDATION_FIELDS } from './ProfilePage.utils';

export type { IErrors, IIndustryCategory } from './ProfilePage.utils';

/** Return type for the useProfilePage hook. */
interface IUseProfilePageReturn {
  /** The current account model */
  account: Account;
  /** Translation function */
  t: ReturnType<typeof useTranslation>['t'];
  /** Function to build local URLs with account prefix */
  localUrl: ReturnType<typeof useLocalUrl>;
  /** Users collection from the store */
  Users: ReturnType<typeof useStore>['Users'];
  /** Currently selected license product */
  selectedProduct: IProduct | undefined;
  /** Setter for selectedProduct */
  setSelectedProduct: Dispatch<SetStateAction<IProduct | undefined>>;
  /** Whether the account has an invalid active package */
  hasInvalidActivePackage: boolean;
  /** Currently selected industry category */
  industryCategory: IIndustryCategory | undefined;
  /** Setter for industryCategory state (does not dirty the account model) */
  setIndustryCategory: Dispatch<SetStateAction<IIndustryCategory | undefined>>;
  /** Currently selected industry subcategory */
  industrySubCategory: IIndustryCategory | undefined;
  /** Setter for industrySubCategory state (does not dirty the account model) */
  setIndustrySubCategory: Dispatch<SetStateAction<IIndustryCategory | undefined>>;
  /** Marks the account's ttd.advertiser_category as dirty for user-initiated industry changes */
  setIndustryDirty: (value: IIndustryCategory) => void;
  /** Handler to update email health status */
  handleEmailHealthChange: (value?: TEmailHealth) => void;
  /** Handler to save all profile settings */
  handleSave: () => Promise<void>;
  /** Handler to update the account manager (CSM) */
  handleAccountManagerChange: (value?: string) => void;
  /** Handler to update the Pro (strategist) */
  handleProChange: (value?: string) => void;
  /** Handler to update campaigns max budget setting */
  handleCampaignsMaxBudgetChange: (newValue?: number) => void;
  /** Handler for logo image selection */
  onPickLogo: (url: string) => void;
  /** Validation errors for profile fields */
  errors: IErrors;
  /** File picker configuration for logo upload */
  pickerOptions: ReturnType<typeof getPickerOptions>;
  /** Action bar setter for right actions */
  setRightActions: ReturnType<typeof useActionBar>['setRightActions'];
}

/**
 * Custom hook that encapsulates all state management and business logic for the ProfilePage component.
 *
 * Handles account profile editing including name, logo, email health, license/billing,
 * team assignments (Account Manager, Pro), campaigns max budget, and industry category.
 *
 * @returns Object containing state values, handlers, and validation errors
 *
 * @example
 * ```tsx
 * const {
 *   account, errors, handleSave,
 *   selectedProduct, setSelectedProduct,
 * } = useProfilePage();
 * ```
 */
export function useProfilePage(): IUseProfilePageReturn {
  const { Users } = useStore();
  const account = useAccount();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const { setRightActions } = useActionBar();
  const [selectedProduct, setSelectedProduct] = useState<IProduct | undefined>();
  const hasInvalidActivePackage = !!account.activePackage && !account.hasValidLicensePackageName;
  const [industryCategory, setIndustryCategory] = useState<IIndustryCategory>();
  const [industrySubCategory, setIndustrySubCategory] = useState<IIndustryCategory>();

  function setIndustryDirty(value: IIndustryCategory): void {
    account.set({
      ttd: {
        ...account.get('ttd'),
        advertiser_category: value.name,
      },
    });
  }

  function handleEmailHealthChange(value: TEmailHealth = 'healthy'): void {
    account.set({ email_health: value });
  }

  async function handleSave(): Promise<void> {
    try {
      const shouldUpdateIndustry =
        industryCategory &&
        (!industryCategory.isSensitive || (industryCategory.isSensitive && industrySubCategory)) &&
        account.isAttributeDirty('ttd');

      account.setAttributeClean('ttd');
      await account.patchDirty();

      if (selectedProduct) {
        await account.updateLicense(selectedProduct);
        setSelectedProduct(undefined);
      }
      if (shouldUpdateIndustry) {
        await account.updateIndustryCategory(industryCategory.name, industrySubCategory?.name);
      }
      toast(t('Saved settings.'), { type: 'success' });
    } catch (error) {
      showError(error, t);
    }
  }

  function handleAccountManagerChange(value?: string): void {
    account.set({ csm: value });
  }

  function handleProChange(value?: string): void {
    account.set({ strategist: value });
  }

  function handleCampaignsMaxBudgetChange(newValue?: number): void {
    if (newValue) {
      account.setSetting('campaigns_max_budget', newValue);
    }
  }

  function onPickLogo(url: string): void {
    account.set({ logo: url });
  }

  const errors: IErrors = account.validate<IErrors>(
    [...VALIDATION_FIELDS],
    false,
    'grouped',
  ).errors;

  const pickerOptions = getPickerOptions(t);

  return {
    account,
    t,
    localUrl,
    Users,
    selectedProduct,
    setSelectedProduct,
    hasInvalidActivePackage,
    industryCategory,
    setIndustryCategory,
    industrySubCategory,
    setIndustrySubCategory,
    setIndustryDirty,
    handleEmailHealthChange,
    handleSave,
    handleAccountManagerChange,
    handleProChange,
    handleCampaignsMaxBudgetChange,
    onPickLogo,
    errors,
    pickerOptions,
    setRightActions,
  };
}
